import React from "react";
import { Link } from "react-router-dom";
import { Camera, Info } from "lucide-react";
import { viewName, viewGuidance } from "@/lib/captureViews";

/**
 * Multi-camera angles on ONE analysis.
 *
 * Two camera angles are stored as two sessions sharing a `session_group_id`
 * (the clip route holds exactly one file per session, so two clips cannot live
 * on one session). This is what turns those two sessions back into a single
 * analysis page: a tab per angle, and — because the views are analysed
 * independently and their numbers are deliberately NOT averaged — a short guide
 * saying which angle to read each metric from.
 *
 * Shared by the coach page, the student page and the public share page so the
 * three cannot drift apart. Presentational only: `linkFor` supplies the route,
 * because the coach navigates by session id while a public visitor navigates by
 * share token.
 */
export default function ViewSwitcher({ views = [], linkFor, testIdPrefix = "view" }) {
  if (!Array.isArray(views) || views.length < 2) return null;
  const current = views.find((v) => v.is_current) || views[0];
  const { pairs, notes } = viewGuidance(views);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-switcher`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] font-bold inline-flex items-center gap-1.5">
          <Camera size={11} /> {views.length} angles
        </span>
        <div className="flex flex-wrap gap-1.5">
          {views.map((v, i) => {
            const isActive = v.id === current?.id;
            const name = viewName(v, i);
            const badge = v.rep_count ? `${v.rep_count} reps` : v.has_clip ? "clip" : "no clip";
            const cls = isActive
              ? "bg-[#38BDF8] text-black"
              : "border border-white/20 text-white/80 hover:border-[#38BDF8]/60 hover:text-white";
            return (
              <Link
                key={v.id}
                to={linkFor ? linkFor(v.id) : "#"}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest rounded ${cls}`}
                data-testid={`${testIdPrefix}-tab-${v.view_index ?? i}`}
                aria-current={isActive ? "true" : undefined}
              >
                {name}
                <span className={isActive ? "text-black/60 ml-2" : "text-white/40 ml-2"}>
                  {badge}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {pairs.length > 0 && (
        <div className="card-surface p-4 space-y-2" data-testid={`${testIdPrefix}-guidance`}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#38BDF8] font-bold inline-flex items-center gap-1.5">
            <Info size={11} /> Reading two angles
          </div>
          {pairs.map((p) => (
            <div key={p.metrics} className="text-xs text-white/80 leading-relaxed">
              <span className="text-white font-bold">{p.metrics}</span>
              {" → read from "}
              <span className="text-[#38BDF8] font-bold">{p.from}</span>
              <span className="text-[#A3A3A3]"> — {p.why}.</span>
            </div>
          ))}
          {notes.map((n) => (
            <div key={n} className="text-[11px] text-[#A3A3A3] leading-relaxed">
              {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
